import t from 'react-translate';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { omit } from 'underscore';

import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import RolePlayContextSetting from './role-play-context-setting';
import CharacterSetting from './ai-character-setting';
import LearnerObjectiveSetting from './ai-learner-objective-setting';

type SetUpInstructionProps = {
  setStep?: Function;
  isUpdate?: boolean;
  onSubmit?: Function;
  onCancel?: Function;
};

const styles = css`
  .btn-container {
    gap: ${halfSpacing}px;
  }
`;

enum SubmitType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
}

const SetUpInstruction = (props: SetUpInstructionProps) => {
  const {
    isUpdate = false,
    onSubmit,
    onCancel,
  } = props || {};
  const dispatch = useDispatch();
  const { getValues, formState } = useFormContext();

  const [submitting, setSubmitting] = useState(false);
  const { isValid, isDirty } = formState;
  const isSubmitDisabled = !isValid || !isDirty;

  const handleCancel = () => {
    if (isDirty) {
      dispatch(openConfirmationDialog({
        onConfirm: () => onCancel(),
        cancelText: t.FORM.CANCEL(),
        confirmText: t.FORM.YES_SURE(),
        title: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.TITLE(),
        bodyText: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.DESCRIPTION(),
      }));
    } else {
      onCancel();
    }
  };

  const getPayload = (formData, submitType) => {
    const cleanedFormData = omit(formData,
      [
        'requiredAiLearnerGoal',
        'aiCharacterObjections',
        'aiLearnerGoals',
      ]);

    switch (submitType) {
      case SubmitType.CREATE:
      {
        const aiCharacterObjections = [
          ...formData.aiCharacterObjections
            .map((item) => (item?.text))
            .filter((text) => text && text.trim() !== ''),
        ];

        const aiLearnerGoals = [
          formData.requiredAiLearnerGoal.text,
          ...formData.aiLearnerGoals
            .map((item) => (item?.text))
            .filter((text) => text && text.trim() !== ''),
        ];
        return {
          ...cleanedFormData,
          aiCharacterObjections,
          aiLearnerGoals,
        };
      }
      case SubmitType.UPDATE:
        return {
          ...cleanedFormData,
        };
      default:
        return {
          ...cleanedFormData,
        };
    }
  };

  const handleCreateRolePlay = () => {
    const payload = getPayload(getValues(), SubmitType.CREATE);
    setSubmitting(true);
    onSubmit({
      aiRolePlay: {
        ...payload,
      },
    }, false, ComponentType.AI_ROLE_PLAY).then(() => {
      setSubmitting(false);
    });
  };

  const handleUpdateRolePlay = () => {
    const payload = getPayload(getValues(), SubmitType.UPDATE);
    setSubmitting(true);
    onSubmit({
      ...payload,
    }).then(() => {
      setSubmitting(false);
      onCancel();
    });
  };

  const getSubmitSection = () => (
    <div className='d-flex pt-8 pb-6 justify-content-center btn-container'>
      <Button
        variant='secondary'
        type='button'
        onClick={handleCancel}
      >
        {t.FORM.CANCEL()}
      </Button>
      {
        isUpdate ? (
          <Button
            variant='primary'
            type='button'
            disabled={isSubmitDisabled || submitting}
            onClick={handleUpdateRolePlay}
          >
            {t.FORM.UPDATE()}
          </Button>
        ) : (
          <Button
            variant='primary'
            type='button'
            disabled={isSubmitDisabled || submitting}
            onClick={handleCreateRolePlay}
          >
            {t.FORM.CREATE()}
          </Button>
        )
      }
    </div>
  );

  return (
    <div css={styles}>
      <RolePlayContextSetting />
      <CharacterSetting />
      <LearnerObjectiveSetting />
      {getSubmitSection()}
    </div>
  );
};

export default SetUpInstruction;
